import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '@/platform/store/types';
import type {
  TemplateErrorCode,
  WhatsappTemplate,
} from '../models/whatsapp-template.model';
import type { WithSlice } from '@reduxjs/toolkit';

export type TemplatesStatus = 'idle' | 'loading' | 'success' | 'error';

export interface TemplatesError {
  code: TemplateErrorCode;
  detail: string;
}

interface TemplatesState {
  list: {
    status: TemplatesStatus;
    items: WhatsappTemplate[];
    error: TemplatesError | null;
  };
  syncing: boolean;
}

const initialState: TemplatesState = {
  list: { status: 'idle', items: [], error: null },
  syncing: false,
};

export const whatsappTemplatesSlice = createSlice({
  name: 'whatsappTemplates',
  initialState,
  reducers: {
    setListLoading: (state) => {
      state.list.status = 'loading';
      state.list.error = null;
    },
    setListData: (state, action: PayloadAction<WhatsappTemplate[]>) => {
      state.list.status = 'success';
      state.list.items = action.payload;
      state.list.error = null;
    },
    setListError: (state, action: PayloadAction<TemplatesError>) => {
      state.list.status = 'error';
      state.list.error = action.payload;
    },
    upsertTemplate: (state, action: PayloadAction<WhatsappTemplate>) => {
      const index = state.list.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index === -1) {
        state.list.items.unshift(action.payload);
      } else {
        state.list.items[index] = action.payload;
      }
    },
    /**
     * Meta deletes by name across every language variant, so the local removal
     * has to match that or the list shows rows that no longer exist.
     */
    removeTemplatesByName: (state, action: PayloadAction<string>) => {
      state.list.items = state.list.items.filter(
        (item) => item.name !== action.payload,
      );
    },
    setSyncing: (state, action: PayloadAction<boolean>) => {
      state.syncing = action.payload;
    },
    resetTemplatesState: () => initialState,
  },
});

export const {
  setListLoading,
  setListData,
  setListError,
  upsertTemplate,
  removeTemplatesByName,
  setSyncing,
  resetTemplatesState,
} = whatsappTemplatesSlice.actions;

// Defensive fallbacks guard against a stale redux-persist snapshot written
// before this slice existed — same pattern as channelMappingSlice.
export const selectTemplatesStatus = (state: RootState): TemplatesStatus =>
  state.whatsappTemplates?.list?.status ?? 'idle';

export const selectTemplates = (state: RootState): WhatsappTemplate[] =>
  state.whatsappTemplates?.list?.items ?? [];

export const selectTemplatesError = (state: RootState): TemplatesError | null =>
  state.whatsappTemplates?.list?.error ?? null;

export const selectTemplatesSyncing = (state: RootState): boolean =>
  state.whatsappTemplates?.syncing ?? false;

export const selectTemplateByMetaId =
  (metaId: string | undefined) =>
  (state: RootState): WhatsappTemplate | undefined =>
    metaId
      ? (state.whatsappTemplates?.list?.items ?? []).find(
          (item) => item.id === metaId,
        )
      : undefined;

// Registers this slice in the typed root state without the platform importing it.
declare module '@/platform/store/rootReducer' {
  interface LazyLoadedSlices extends WithSlice<typeof whatsappTemplatesSlice> {}
}
